// istanbul ignore file — composition shell; logic lives in `nav_definitions.ts` + flag-driven hooks.

import { I18n } from '@calendly/i18n'
import { NavItem, NavItemGroup, NavItemsList, VerticalNav } from '@calendly/ui/components/navigation'
import { groupBy } from 'lodash-es'

import {
  navigationAnalyticsIdForSettingsSection,
  trackSettingsSectionNavigation,
  useTrack,
} from 'hooks/analytics_events'
import { toSentenceCase } from 'shared/utils/to_sentence_case'

import {
  SETTINGS_SECTION,
  mergeSettingsSearch,
} from './constants'
import { SettingsNavIcon } from './settings_nav_icon'
import { desktopNavCss, mobileListCss } from './styles'

/**
 * Account-section IDs grouped under the "Features" sub-header rather than the default "My account"
 * group. Single-source for callers that want to know "is this row a feature row?".
 */
const FEATURE_NAV_ACCOUNT_SECTIONS = new Set([
  SETTINGS_SECTION.ai_notetaker,
  SETTINGS_SECTION.contacts_settings,
])
const SECTION_MAP = { feature: FEATURE_NAV_ACCOUNT_SECTIONS }

const ADMIN_PEOPLE_NAV_SECTIONS = new Set([
  SETTINGS_SECTION.adminPeople,
  SETTINGS_SECTION.adminUsers,
  SETTINGS_SECTION.adminInvitations,
  SETTINGS_SECTION.adminMigrations,
  SETTINGS_SECTION.adminGroups,
  SETTINGS_SECTION.adminPermissions,
])

const ORG_DIRECTORY_NAV_SECTIONS = new Set([
  SETTINGS_SECTION.orgDirectory,
  'organization/users',
  'organization/invitations',
  'organization/migrations',
])

/** Map URL section → VerticalNav {@link NavItem} `itemId`. */
function settingsModalNavSelectedItemId(activeSection) {
  if (activeSection == null) {
    return null
  }
  if (ADMIN_PEOPLE_NAV_SECTIONS.has(activeSection)) {
    return SETTINGS_SECTION.adminPeople
  }
  if (ORG_DIRECTORY_NAV_SECTIONS.has(activeSection)) {
    return SETTINGS_SECTION.orgDirectory
  }
  return activeSection
}

/**
 * Settings-modal `<VerticalNav>` for the desktop side panel and the mobile list view. Renders three
 * groups (account / features / admin) with `<RouterLink>`-friendly hrefs but defers actual
 * navigation to `goToSection` so the dirty-form prompt fires before the URL changes.
 *
 * @param {object} props
 * @param {string | null} props.activeSection — current `SETTINGS_SECTION` value (drives selection)
 * @param {boolean} props.vertical — true for the mobile list view (uses `mobileListCss`)
 * @param {ReturnType<typeof import('./nav_definitions').buildSettingsModalAccountNavItems>} props.accountNavItems
 * @param {ReturnType<typeof import('./nav_definitions').buildSettingsModalAdminNavItems>} props.adminNavItems
 * @param {{ pathname: string, search: string, hash?: string }} props.location — host browser location
 * @param {(next: string) => void} props.goToSection — `useSettingsModalUrlNormalization` callback
 */
export const SettingsModalNav = ({
  activeSection,
  vertical,
  accountNavItems,
  adminNavItems,
  location,
  goToSection,
}) => {
  const track = useTrack()
  const sourceUrl = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`

  const handleSectionChange = (itemId) => {
    trackSettingsSectionNavigation(track, itemId, sourceUrl)
    goToSection(itemId)
  }

  const navClassName = vertical ? mobileListCss : desktopNavCss
  const grouped = groupBy(accountNavItems, ({ section: sec }) => {
    for (const [key, set] of Object.entries(SECTION_MAP)) {
      if (set.has(sec)) return key
    }
    return 'account'
  })
  const featureSettingsItems = grouped.feature ?? []
  const accountSettingsItems = grouped.account ?? []

  const selectedItemId = settingsModalNavSelectedItemId(activeSection)
  const hrefForSection = (sec) => (
    `${location.pathname}${mergeSettingsSearch(sec, location.search)}${location.hash ?? ''}`
  )

  /** CUI {@link NavItemsList} avoids {@link NavItemGroup} flex gap between every row (matches app shell nav). */
  const renderNavItems = (items) => (
    <NavItemsList>
      {items.map(({ section: sec, testId, label }) => (
        <NavItem
          key={sec}
          itemId={sec}
          href={hrefForSection(sec)}
          label={toSentenceCase(label)}
          icon={<SettingsNavIcon section={sec} active={false} />}
          selectedIcon={<SettingsNavIcon section={sec} active />}
          onClick={(e) => e.preventDefault()}
          data-testid={testId}
          data-analytics-id={navigationAnalyticsIdForSettingsSection(sec)}
        />
      ))}
    </NavItemsList>
  )

  return (
    <VerticalNav
      role="navigation"
      aria-label={I18n.t('publisher.personal_settings.navigation.account_settings')}
      className={navClassName}
      expandable={false}
      selectedItemId={selectedItemId}
      onSelectedItemChange={(itemId) => handleSectionChange(itemId)}
    >
      <NavItemGroup
        label={toSentenceCase(I18n.t('publisher.personal_settings.navigation.my_account'))}
        groupId="settings-modal-my-account"
      >
        {renderNavItems(accountSettingsItems)}
      </NavItemGroup>
      {featureSettingsItems.length > 0 ? (
        <NavItemGroup
          label={toSentenceCase(I18n.t('publisher.personal_settings.navigation.features'))}
          groupId="settings-modal-features"
        >
          {renderNavItems(featureSettingsItems)}
        </NavItemGroup>
      ) : null}
      {adminNavItems.length > 0 ? (
        <NavItemGroup
          label={toSentenceCase(I18n.t('publisher.personal_settings.navigation.admin'))}
          groupId="settings-modal-admin"
        >
          {renderNavItems(adminNavItems)}
        </NavItemGroup>
      ) : null}
    </VerticalNav>
  )
}
